import CaptureButtonIcon from 'assets/ver2/icons/capture-button-icon.svg';
import {
  DESKTOP_MIN_WIDTH,
  MOBILE_MIN_WIDTH,
  TABLET_MAX_WIDTH,
} from 'constants';
import useCameraMaskToCapturePhoto from 'features/uploadDocuments/hooks/useCameraMaskToCapturePhoto';
import useCommonStore from 'hooks/useCommonStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Webcam from 'react-webcam';
import styled from 'styled-components';
import {
  cursorPointerMixin,
  displayFlexColCenterCenterMixin,
  displayFlexRowCenterStartMixin,
  fontSize14Mixin,
  fontSize16Mixin,
  paragraph500Mixin,
  scrollbarThumbMixin,
} from 'styles';
import InstructionToCapturePhotoPopup from './InstructionToCapturePhotoPopup';
import PhotoCapturedResultPopup from './PhotoCapturedResultPopup';

const RECT_WIDTH_DESKTOP = 400;
const RECT_WIDTH_MOBILE = 345;
const RECT_HEIGHT = 300;

const OVAL_WIDTH = 250;
const OVAL_HEIGHT = 300;

const DOCTYPE_TO_CAPTURETYPE_MAPPING = {
  DL: 'id',
  P: 'id',
  SELFIE: 'selfie',
};

const CustomCameraMaskToCapturePhotoPopup = ({
  isOpen,
  isBackdropOverlap,
  zIndexBackdropOverlap,
  zIndexModal = 0,
  onClose,
  modalData,
}) => {
  const { t } = useLang();
  const { isDesktop } = useDetectDevice();
  const { setLoading } = useCommonStore();

  const webcamRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [
    isOpenInstructionToCapturePhotoPopup,
    setOpenInstructionToCapturePhotoPopup,
  ] = useState(false);
  const [isOpenPhotoCapturedResultPopup, setOpenPhotoCapturedResultPopup] =
    useState(false);

  let { field, information, setInformation } = modalData || {};
  const { docType } = field || {};
  const captureType = DOCTYPE_TO_CAPTURETYPE_MAPPING[docType];
  const isCaptureTypeID = captureType === 'id';

  const { handleShowCheckCameraToTakePhotoEnableAlertModal } =
    useCameraMaskToCapturePhoto();

  const capture = useCallback(() => {
    const imgSrc = webcamRef.current.getScreenshot();
    setImgSrc(imgSrc);
  }, [webcamRef, setImgSrc]);
  const handleOnCapture = () => {
    console.log(
      `⭐ CustomCameraMaskToCapturePhotoPopup 👉 handleOnCapture ➡️ RUNNING ↩`
    );
    capture();
    setOpenPhotoCapturedResultPopup(true);
  };

  const handleOnToggleInstructionToCapturePhotoPopup = () => {
    setOpenInstructionToCapturePhotoPopup(
      !isOpenInstructionToCapturePhotoPopup
    );
  };
  const handleOnClickInstructionButton = () => {
    handleOnToggleInstructionToCapturePhotoPopup();
  };
  const renderInstructionToCapturePhotoPopup = () => {
    if (!isOpenInstructionToCapturePhotoPopup) return null;
    return (
      <InstructionToCapturePhotoPopup
        isOpen={isOpenInstructionToCapturePhotoPopup}
        onClose={handleOnToggleInstructionToCapturePhotoPopup}
        modalData={{ captureType }}
      />
    );
  };

  const handleOnTogglePhotoCapturedResultPopup = () => {
    setOpenPhotoCapturedResultPopup(!isOpenPhotoCapturedResultPopup);
  };
  const handleOnSavePhotoCaptured = () => {
    setInformation({
      ...information,
      [field.name]: imgSrc,
    });
  };
  const renderPhotoCapturedResultPopup = () => {
    if (!isOpenPhotoCapturedResultPopup) return null;
    return (
      <PhotoCapturedResultPopup
        isOpen={isOpenPhotoCapturedResultPopup}
        onClose={handleOnTogglePhotoCapturedResultPopup}
        onClick={handleOnSavePhotoCaptured}
        modalData={{ imgSrc, captureType }}
      />
    );
  };

  const renderCaptureRequires = () => {
    if (isCaptureTypeID)
      return (
        <CaptureRequires>
          <CaptureRequireItem>
            {t('label_upload_documents_capture_id_requires_1')}
          </CaptureRequireItem>
          <CaptureRequireItem>
            {t('label_upload_documents_capture_id_requires_2')}
            <img src={CaptureButtonIcon} width={18} height={18} />
          </CaptureRequireItem>
        </CaptureRequires>
      );
    return (
      <CaptureRequires>
        <CaptureRequireItem>
          {t('label_upload_documents_capture_selfie_requires_1')}
        </CaptureRequireItem>
        <CaptureRequireItem>
          {t('label_upload_documents_capture_selfie_requires_2')}
          <img src={CaptureButtonIcon} width={18} height={18} />
        </CaptureRequireItem>
      </CaptureRequires>
    );
  };

  const getWebcamSize = () => {
    if (isDesktop) {
      if (isCaptureTypeID)
        return { width: RECT_WIDTH_DESKTOP, height: RECT_HEIGHT };
      return { width: OVAL_WIDTH, height: OVAL_HEIGHT };
    }
    if (isCaptureTypeID)
      return { width: RECT_WIDTH_MOBILE, height: RECT_HEIGHT };
    return { width: OVAL_WIDTH, height: OVAL_HEIGHT };
  };
  const getWebcamFacingMode = () => {
    if (isDesktop) return 'user';
    if (isCaptureTypeID) return { exact: 'environment' };
    return 'user';
  };
  const renderWebcamMask = () => {
    if (isCaptureTypeID) return <WebcamRectMask />;
    return <WebcamOvalMask />;
  };
  const getWebcamStyles = () => {
    if (isCaptureTypeID)
      return {
        borderRadius: '20px',
        outline: 'unset',
        border: 'unset',
        width: `${getWebcamSize().width}px`,
        height: `${getWebcamSize().height}px`,
        objectFit: 'cover',
      };
    return {
      borderRadius: '200px',
      outline: '5px solid #ffffff',
      width: `${getWebcamSize().width}px`,
      height: `${getWebcamSize().height}px`,
      objectFit: 'cover',
    };
  };

  const handleOnUserMedia = (stream) => {
    console.log(
      `⭐ CustomCameraMaskToCapturePhotoPopup 👉 handleOnUserMedia ➡️ stream ↩`,
      stream
    );
    setLoading(false);
  };
  const handleOnUserMediaError = (error) => {
    console.error(
      `🔥 CustomCameraMaskToCapturePhotoPopup ❌ handleOnUserMediaError ➡️ ERROR ↩`,
      error
    );
    setLoading(false);
    handleShowCheckCameraToTakePhotoEnableAlertModal();
  };
  useEffect(() => {
    setLoading(true);
  }, []);

  return (
    <>
      {renderInstructionToCapturePhotoPopup()}
      {renderPhotoCapturedResultPopup()}
      <style>
        {zIndexModal &&
          `
          .custom-camera-mask-to-capture-photo-modal {
            z-index: ${zIndexModal || '1101'};
          }
        `}
        {isBackdropOverlap
          ? `
          .custom-camera-mask-to-capture-photo-modal-backdrop {
            z-index: ${zIndexBackdropOverlap || '1100'};
            background: #212121;
            opacity: 0.7!important;
            display: initial!important;
          }
        `
          : `
        .custom-camera-mask-to-capture-photo-modal-backdrop {
          background: #212121;
          opacity: 0.7!important;
          display: initial!important;
        }
      `}
      </style>
      <ModalStyled
        show={isOpen}
        onHide={() => {}}
        aria-labelledby="contained-modal-title-vcenter"
        className="custom-camera-mask-to-capture-photo-modal"
        backdropClassName="custom-camera-mask-to-capture-photo-modal-backdrop"
        centered
      >
        <ModalBody>
          <CustomCameraMaskToCapturePhotoStyled>
            <BodyContent>
              <WebcamOverlay />
              {renderWebcamMask()}
              <WebcamStyled
                ref={webcamRef}
                audio={false}
                disablePictureInPicture={true}
                imageSmoothing={true}
                screenshotFormat="image/jpeg"
                minScreenshotWidth={getWebcamSize().width}
                minScreenshotHeight={getWebcamSize().height}
                width={getWebcamSize().width}
                height={getWebcamSize().height}
                screenshotQuality={1}
                videoConstraints={{
                  facingMode: getWebcamFacingMode(),
                  width: getWebcamSize().width,
                  height: getWebcamSize().height,
                }}
                onUserMedia={handleOnUserMedia}
                onUserMediaError={handleOnUserMediaError}
                style={getWebcamStyles()}
              />
              <CaptureButton
                src={CaptureButtonIcon}
                onClick={handleOnCapture}
                width={77}
                height={77}
              />
              <CloseButton onClick={onClose}>{t('button_close')}</CloseButton>
              {renderCaptureRequires()}
              <InstructionButton onClick={handleOnClickInstructionButton}>
                {t('label_instruction')}
              </InstructionButton>
            </BodyContent>
          </CustomCameraMaskToCapturePhotoStyled>
        </ModalBody>
      </ModalStyled>
    </>
  );
};

const ModalStyled = styled(Modal)`
  padding-left: 0px !important;
  padding-right: 0px !important;

  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    & .modal-dialog {
      position: absolute;
      margin: 0;
      top: 0px;
      height: 100dvh;
      width: 100%;
      max-width: 100%;
      align-items: end;
      transition: ease-out 0.3s !important;
      transform: none;

      & .modal-content {
        padding: 0px 16px;
        box-shadow: none;
        background: var(--ds-c-white);
        width: 100%;
        min-height: 100dvh;
        max-height: 100dvh;
        margin-inline: auto;
        border: none;
        border-radius: 0;
      }
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & .modal-dialog {
      transform: none;
      & .modal-content {
        padding: 0px 16px;
        box-shadow: var(--ds-bs-4);
        background: var(--ds-c-white);
        width: 430px;
        height: 657px;
        margin-inline: auto;
        border: none;
        border-radius: 20px;
      }
    }
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 0;
  text-align: left;
  margin-bottom: 0px;
  overflow-x: auto;
  margin-inline: -16px;
  padding-inline: 16px;
  ${scrollbarThumbMixin};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    overflow-x: unset;
    border-radius: 20px;
  }
`;

const CustomCameraMaskToCapturePhotoStyled = styled.div`
  ${displayFlexColCenterCenterMixin};
  width: 100%;
  height: 100dvh;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    height: 100%;
  }
`;

const BodyContent = styled.div`
  ${displayFlexColCenterCenterMixin};
  max-width: 100%;
`;

const WebcamStyled = styled(Webcam)`
  position: absolute;
  top: 54px;
`;
const WebcamOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(69, 69, 69, 0) 10%, #46494d 100%);
  border-radius: 0;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    border-radius: 0 0 18px 18px;
  }
`;
const WebcamRectMask = styled.div`
  box-shadow: 0 0 0 100dvh #2c2c2ccc;
  position: absolute;
  top: 54px;
  border-radius: 25px;
  margin-inline: auto;
  width: ${RECT_WIDTH_MOBILE - 1}px;
  height: ${RECT_HEIGHT}px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: ${RECT_WIDTH_DESKTOP}px;
  }
`;
const WebcamOvalMask = styled.div`
  box-shadow: 0 0 0 100dvh #2c2c2ccc;
  position: absolute;
  top: 54px;
  border-radius: 200px;
  margin-inline: auto;
  width: ${OVAL_WIDTH}px;
  height: ${OVAL_HEIGHT}px;
`;
const CaptureButton = styled.img`
  ${cursorPointerMixin};
  position: fixed;
  bottom: 40px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    position: absolute;
  }
`;

const CloseButton = styled.span`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  ${cursorPointerMixin};
  color: #fff;
  position: absolute;
  right: 16px;
  top: 16px;
`;

const CaptureRequires = styled.div`
  position: absolute;
  top: 375px;
  left: 16px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: calc(100% - 32px);
  }
`;
const CaptureRequireItem = styled.p`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  ${displayFlexRowCenterStartMixin};
  gap: 4px;
  color: #fff;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    ${fontSize16Mixin};
  }
`;

const InstructionButton = styled.span`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  ${cursorPointerMixin};
  color: #00c8ff;
  position: absolute;
  left: 16px;
  top: 435px;
  text-decoration: underline;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    ${fontSize16Mixin};
  }
`;

export default CustomCameraMaskToCapturePhotoPopup;
